import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import api from "../lib/api";
import Header from "../components/dashboard/Header";
import MemberDetail from "../components/MemberDetail";
import { ExecKpis } from "../components/executive/ExecKpis";
import { ExecLocationTable } from "../components/executive/ExecLocationTable";
import { ExecSales, ExecOps, ExecAttention } from "../components/executive/ExecBlocks";
import { fmtPeriod } from "../lib/format";

const ago = (iso) => { if (!iso) return "—"; const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 1 ? "just now" : m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`; };

function Skeleton() {
  return (
    <div className="space-y-6" data-testid="exec-loading">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="h-32 rounded-xl bg-brand-surface border border-brand-border animate-pulse" />)}</div>
      <div className="h-80 rounded-xl bg-brand-surface border border-brand-border animate-pulse" />
      <p className="text-xs text-brand-tertiary">Pulling from Yardi, Kisi, UniFi, Notion and Ramp — first load takes ~20 seconds.</p>
    </div>
  );
}

export default function Executive() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const load = useCallback(async (refresh = false) => {
    setBusy(true);
    try { const { data } = await api.get("/executive", { params: refresh ? { refresh: true } : {} }); setD(data); }
    catch (e) { setD(false); setErr(e.userMessage); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-brand-page">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 space-y-10">
        <div className="flex flex-wrap items-end justify-between gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-brand-tertiary mb-2">{today}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-ink tracking-tight" data-testid="exec-title">Executive Board</h1>
            <p className="text-sm text-brand-muted mt-3 max-w-xl">Finance, members, sales and building operations across every location — on one page.</p>
          </div>
          <div className="text-right">
            <button onClick={() => load(true)} disabled={busy} data-testid="exec-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-brand-border bg-brand-surface px-4 py-2 text-sm text-brand-ink hover:bg-brand-ink hover:text-brand-page disabled:opacity-60 transition-colors">
              <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Refresh
            </button>
            {d && <p className="text-[11px] text-brand-tertiary mt-2" data-testid="exec-updated">Updated {ago(d.generated_at)} · financials {fmtPeriod(d.period)} · members {ago(d.members_synced_at)}</p>}
          </div>
        </div>

        {d === null && <Skeleton />}
        {d === false && <p className="text-sm text-brand-neg" data-testid="exec-error">Couldn't load the board: {err} Try Refresh.</p>}
        {d && (
          <>
            <ExecKpis k={d.kpis} period={fmtPeriod(d.period)} />
            <section>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-lg text-brand-ink font-medium">Locations</h2>
                <span className="text-xs text-brand-tertiary">Click a row to open the location</span>
              </div>
              <ExecLocationTable rows={d.locations} />
            </section>
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <ExecSales s={d.sales || {}} />
              <ExecOps o={d.ops} />
              <ExecAttention rows={d.attention} total={d.attention_total} onOpen={setOpen} />
            </section>
          </>
        )}
      </main>
      {open && <MemberDetail companyId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
